import { z } from 'zod';
import {
  insertOrganizationSchema,
  insertUserSchema,
  insertBuildingSchema,
  insertApartmentSchema,
  insertSectionSchema,
  insertChecklistItemSchema,
  insertPhotoRequirementSchema,
  insertSubmissionSchema,
  insertCleaningTaskSchema,
  insertCleaningJobSchema,
  insertReservationSchema,
  insertCommissionSettingsSchema,
  insertFinancialRecordSchema,
  insertBillingPlanSchema,
  insertBillingInvoiceSchema,
  insertGeneralTaskSchema,
  organizations,
  users,
  buildings,
  apartments,
  sections,
  checklistItems,
  photoRequirements,
  submissions,
  cleaningTasks,
  cleaningJobs,
  reservations,
  commissionSettings,
  financialRecords,
  billingPlans,
  billingInvoices,
  notifications,
  pushSubscriptions,
  checkinSubmissions,
  tickets,
  ticketMessages,
  securityDeposits,
  cashTransactions,
  generalTasks,
  orgContracts,
  orgDataGrants,
} from './schema';
import { adminCreateGeneralTaskInput, adminUpdateGeneralTaskInput } from './general-task-recurrence';

export const errorSchemas = {
  validation: z.object({
    message: z.string(),
    field: z.string().optional(),
  }),
  notFound: z.object({
    message: z.string(),
  }),
  unauthorized: z.object({
    message: z.string(),
  }),
};

export const api = {
  auth: {
    login: {
      method: 'POST' as const,
      path: '/api/login' as const,
      input: z.object({ username: z.string(), password: z.string() }),
      responses: {
        200: z.custom<typeof users.$inferSelect>(),
        401: errorSchemas.unauthorized,
      },
    },
    logout: {
      method: 'POST' as const,
      path: '/api/logout' as const,
      responses: {
        200: z.void(),
      },
    },
    me: {
      method: 'GET' as const,
      path: '/api/user' as const,
      responses: {
        200: z.custom<typeof users.$inferSelect>(),
        401: errorSchemas.unauthorized,
      },
    },
  },
  buildings: {
    list: {
      method: 'GET' as const,
      path: '/api/buildings' as const,
      responses: {
        200: z.array(z.custom<typeof buildings.$inferSelect>()),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/buildings' as const,
      input: insertBuildingSchema,
      responses: {
        201: z.custom<typeof buildings.$inferSelect>(),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/buildings/:id' as const,
      responses: {
        200: z.custom<typeof buildings.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/buildings/:id' as const,
      input: insertBuildingSchema.partial(),
      responses: {
        200: z.custom<typeof buildings.$inferSelect>(),
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/buildings/:id' as const,
      responses: {
        204: z.void(),
      },
    },
  },
  apartments: {
    list: {
      method: 'GET' as const,
      path: '/api/apartments' as const,
      responses: {
        200: z.array(z.custom<typeof apartments.$inferSelect>()),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/apartments' as const,
      input: insertApartmentSchema,
      responses: {
        201: z.custom<typeof apartments.$inferSelect>(),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/apartments/:id' as const,
      responses: {
        200: z.custom<typeof apartments.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/apartments/:id' as const,
      input: insertApartmentSchema.partial(),
      responses: {
        200: z.custom<typeof apartments.$inferSelect>(),
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/apartments/:id' as const,
      responses: {
        204: z.void(),
      },
    },
  },
  chat: {
    send: {
      method: 'POST' as const,
      path: '/api/g/:slug/chat' as const,
      input: z.object({
        token: z.string(),
        message: z.string(),
        history: z.array(z.object({
          role: z.enum(['user', 'assistant']),
          content: z.string(),
        })).optional(),
      }),
      responses: {
        200: z.object({ reply: z.string() }),
        403: errorSchemas.unauthorized,
      },
    },
  },
  guest: {
    getApartment: {
      method: 'GET' as const,
      path: '/api/g/:slug' as const,
      responses: {
        200: z.object({
          apartment: z.custom<typeof apartments.$inferSelect>(),
          building: z.custom<typeof buildings.$inferSelect>(),
          sections: z.array(z.custom<typeof sections.$inferSelect>()),
          checklistItems: z.array(z.custom<typeof checklistItems.$inferSelect>()),
          photoRequirements: z.array(z.custom<typeof photoRequirements.$inferSelect>()),
        }),
        404: errorSchemas.notFound,
        403: errorSchemas.unauthorized,
      },
    },
    submitCheckout: {
      method: 'POST' as const,
      path: '/api/g/:slug/checkout' as const,
      input: z.object({
        token: z.string(),
        guestName: z.string(),
        guestPhone: z.string(),
        guestEmail: z.string().optional(),
        fobFrontUrl: z.string().optional(),
        fobBackUrl: z.string().optional(),
        checklistResults: z.array(z.object({ itemId: z.number(), isChecked: z.boolean() })),
        photoResults: z.array(z.object({ requirementId: z.number(), urls: z.array(z.string()) })),
      }),
      responses: {
        201: z.custom<typeof submissions.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
  },
  admin: {
    users: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/users' as const,
        responses: { 200: z.array(z.custom<typeof users.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/users' as const,
        input: z.object({
          username: z.string(),
          password: z.string(),
          role: z.enum(['super_admin', 'staff', 'owner', 'cleaner', 'maintenance']),
          fullName: z.string().optional(),
          email: z.string().optional(),
          phone: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof users.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/users/:id' as const,
        input: z.object({
          username: z.string().optional(),
          fullName: z.string().optional(),
          email: z.string().optional(),
          phone: z.string().optional(),
          role: z.enum(['super_admin', 'staff', 'owner', 'cleaner', 'maintenance']).optional(),
          password: z.string().optional(),
          disabledFeatures: z.array(z.string()).optional(),
          isActive: z.boolean().optional(),
        }),
        responses: { 200: z.custom<typeof users.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/users/:id' as const,
        responses: { 204: z.void() },
      },
    },
    sections: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/sections' as const,
        responses: { 200: z.array(z.custom<typeof sections.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/sections' as const,
        input: insertSectionSchema,
        responses: { 201: z.custom<typeof sections.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/sections/:id' as const,
        input: insertSectionSchema.partial(),
        responses: { 200: z.custom<typeof sections.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/sections/:id' as const,
        responses: { 204: z.void() },
      },
    },
    checklist: {
      create: {
        method: 'POST' as const,
        path: '/api/admin/checklist' as const,
        input: insertChecklistItemSchema,
        responses: { 201: z.custom<typeof checklistItems.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/checklist/:id' as const,
        responses: { 204: z.void() },
      },
    },
    photoRequirements: {
      create: {
        method: 'POST' as const,
        path: '/api/admin/photo-requirements' as const,
        input: insertPhotoRequirementSchema,
        responses: { 201: z.custom<typeof photoRequirements.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/photo-requirements/:id' as const,
        responses: { 204: z.void() },
      },
    },
    submissions: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/submissions' as const,
        responses: { 200: z.array(z.custom<typeof submissions.$inferSelect>()) },
      },
      get: {
        method: 'GET' as const,
        path: '/api/admin/submissions/:id' as const,
        responses: { 200: z.custom<typeof submissions.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/submissions/:id' as const,
        responses: { 200: z.object({ message: z.string() }) },
      },
    },
    cleaning: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/cleaning' as const,
        responses: { 200: z.array(z.custom<typeof cleaningTasks.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/cleaning' as const,
        input: insertCleaningTaskSchema,
        responses: { 201: z.custom<typeof cleaningTasks.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/cleaning/:id' as const,
        input: insertCleaningTaskSchema.partial(),
        responses: { 200: z.custom<typeof cleaningTasks.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/cleaning/:id' as const,
        responses: { 204: z.void() },
      },
    },
    cleaningJobs: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/cleaning-jobs' as const,
        responses: { 200: z.array(z.custom<typeof cleaningJobs.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/cleaning-jobs' as const,
        input: z.object({
          apartmentId: z.number().optional().nullable(),
          reservationId: z.number().nullable().optional(),
          assignedTo: z.number().nullable().optional(),
          secondCleanerId: z.number().nullable().optional(),
          priority: z.enum(['low', 'normal', 'urgent']).optional(),
          notes: z.string().optional(),
          address: z.string().optional().nullable(),
          scheduledDate: z.string().optional(),
          ownerUserId: z.number().nullable().optional(),
          jobType: z.string().optional(),
          customFee: z.number().nullable().optional(),
          customCleanerPay: z.number().nullable().optional(),
          adminNotes: z.string().optional().nullable(),
          assignedOrgId: z.number().nullable().optional(),
        }),
        responses: { 201: z.custom<typeof cleaningJobs.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/cleaning-jobs/:id' as const,
        input: insertCleaningJobSchema.partial(),
        responses: { 200: z.custom<typeof cleaningJobs.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/cleaning-jobs/:id' as const,
        responses: { 204: z.void() },
      },
    },
    generalTasks: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/general-tasks' as const,
        responses: { 200: z.array(z.custom<typeof generalTasks.$inferSelect>()) },
      },
      get: {
        method: 'GET' as const,
        path: '/api/admin/general-tasks/:id' as const,
        responses: { 200: z.custom<typeof generalTasks.$inferSelect>() },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/general-tasks' as const,
        input: adminCreateGeneralTaskInput,
        responses: { 201: z.custom<typeof generalTasks.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/general-tasks/:id' as const,
        input: adminUpdateGeneralTaskInput,
        responses: { 200: z.custom<typeof generalTasks.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/general-tasks/:id' as const,
        responses: { 204: z.void() },
      },
    },
    laundryJobs: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/laundry-jobs' as const,
        responses: { 200: z.array(z.custom<Record<string, unknown>>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/laundry-jobs' as const,
        input: z.object({
          cleaningJobId: z.number().nullable().optional(),
          assignedTo: z.number().nullable().optional(),
          takenItems: z.array(z.object({ name: z.string(), qty: z.number() })),
          notes: z.string().nullable().optional(),
          scheduledAt: z.string().nullable().optional(),
        }),
        responses: { 201: z.custom<Record<string, unknown>>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/laundry-jobs/:id' as const,
        input: z.object({
          status: z.enum(['pending', 'in_progress', 'completed']).optional(),
          assignedTo: z.number().nullable().optional(),
          takenItems: z.array(z.object({ name: z.string(), qty: z.number() })).optional(),
          returnedItems: z.array(z.object({ name: z.string(), qty: z.number() })).optional(),
          notes: z.string().nullable().optional(),
        }),
        responses: { 200: z.custom<Record<string, unknown>>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/laundry-jobs/:id' as const,
        responses: { 204: z.void() },
      },
    },
    laundrySettings: {
      get: {
        method: 'GET' as const,
        path: '/api/admin/laundry-settings' as const,
        responses: { 200: z.custom<Record<string, unknown>>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/laundry-settings' as const,
        input: z.object({
          showTakenItemsToLaundry: z.boolean().optional(),
          assignmentMode: z.enum(['manual', 'specific_user', 'round_robin']).optional(),
          specificUserId: z.number().nullable().optional(),
        }),
        responses: { 200: z.custom<Record<string, unknown>>() },
      },
    },
    reservations: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/reservations' as const,
        responses: { 200: z.array(z.custom<typeof reservations.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/reservations' as const,
        input: z.object({
          apartmentId: z.number(),
          guestName: z.string().min(1),
          guestEmail: z.string().optional(),
          guestPhone: z.string().optional(),
          checkInDate: z.string(),
          checkOutDate: z.string(),
          status: z.enum(['confirmed', 'checked_in', 'checked_out', 'cancelled']).optional(),
          notes: z.string().optional(),
          source: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof reservations.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/reservations/:id' as const,
        input: z.object({
          guestName: z.string().optional(),
          guestEmail: z.string().optional(),
          guestPhone: z.string().optional(),
          checkInDate: z.string().optional(),
          checkOutDate: z.string().optional(),
          status: z.enum(['confirmed', 'checked_in', 'checked_out', 'cancelled']).optional(),
          notes: z.string().optional(),
        }),
        responses: { 200: z.custom<typeof reservations.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/reservations/:id' as const,
        responses: { 204: z.void() },
      },
      ical: {
        method: 'GET' as const,
        path: '/api/admin/reservations/ical' as const,
        responses: { 200: z.string() },
      },
    },
    finances: {
      records: {
        method: 'GET' as const,
        path: '/api/admin/finances' as const,
        responses: { 200: z.array(z.custom<typeof financialRecords.$inferSelect>()) },
      },
      summary: {
        method: 'GET' as const,
        path: '/api/admin/finances/summary' as const,
        responses: {
          200: z.object({
            totalRevenue: z.string(),
            totalAdminCommission: z.string(),
            totalOrgExpenses: z.string(),
            netAdminProfit: z.string(),
            totalCleanerPayments: z.string(),
            totalCheckInFees: z.string(),
            totalCleaningFees: z.string(),
            pendingPayments: z.string(),
            recordCount: z.number(),
          }),
        },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/finances' as const,
        input: z.object({
          type: z.enum(['check_in', 'cleaning', 'intra_stay_cleaning', 'unit_setup', 'maintenance', 'miscellaneous', 'credit']),
          apartmentId: z.number(),
          reservationId: z.number().optional(),
          cleaningJobId: z.number().optional(),
          ownerUserId: z.number().optional(),
          cleanerUserId: z.number().optional(),
          totalAmount: z.number().optional(),
          description: z.string().optional(),
          paymentMethod: z.enum(['cash', 'bank_transfer', 'zelle', 'airbnb_payout', 'venmo', 'other']).optional(),
          status: z.enum(['pending', 'paid', 'cancelled']).optional(),
          createdAt: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof financialRecords.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/finances/:id' as const,
        input: z.object({
          status: z.enum(['pending', 'paid', 'cancelled']).optional(),
          description: z.string().optional(),
          paymentMethod: z.enum(['cash', 'bank_transfer', 'zelle', 'airbnb_payout', 'venmo', 'other']).optional(),
          totalAmount: z.string().optional(),
          adminAmount: z.string().optional(),
          cleanerAmount: z.string().optional(),
          partnerAmount: z.string().optional(),
          ownerUserId: z.number().nullable().optional(),
          cleanerUserId: z.number().nullable().optional(),
          type: z.enum(['check_in', 'cleaning', 'intra_stay_cleaning', 'unit_setup', 'maintenance', 'miscellaneous', 'booking_commission', 'credit']).optional(),
        }),
        responses: { 200: z.custom<typeof financialRecords.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/finances/:id' as const,
        responses: { 200: z.object({ success: z.boolean() }) },
      },
      revert: {
        method: 'POST' as const,
        path: '/api/admin/finances/:id/revert' as const,
        input: z.object({ reason: z.string().min(3).max(500) }),
        responses: { 200: z.custom<typeof financialRecords.$inferSelect>() },
      },
      settings: {
        get: {
          method: 'GET' as const,
          path: '/api/admin/commission-settings' as const,
          responses: { 200: z.custom<typeof commissionSettings.$inferSelect>() },
        },
        update: {
          method: 'PUT' as const,
          path: '/api/admin/commission-settings' as const,
          input: z.object({
            checkInFee: z.string().optional(),
            cleaningFee: z.string().optional(),
            cleanerPay: z.string().optional(),
            cleaningFeeStudio: z.string().optional(),
            cleanerPayStudio: z.string().optional(),
            cleaningFee2br: z.string().optional(),
            cleanerPay2br: z.string().optional(),
            cleaningFee3br: z.string().optional(),
            cleanerPay3br: z.string().optional(),
            cleaningFee4br: z.string().optional(),
            cleanerPay4br: z.string().optional(),
            cleaningFee5br: z.string().optional(),
            cleanerPay5br: z.string().optional(),
            partnerCommissionPct: z.string().optional(),
            partnerName: z.string().optional(),
            intraStayFee: z.string().optional(),
            intraStayCleanerPay: z.string().optional(),
          }),
          responses: { 200: z.custom<typeof commissionSettings.$inferSelect>() },
        },
      },
    },
    reports: {
      get: {
        method: 'GET' as const,
        path: '/api/admin/reports' as const,
        responses: { 200: z.any() },
      },
    },
    deposits: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/deposits' as const,
        responses: { 200: z.array(z.custom<typeof securityDeposits.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/deposits' as const,
        input: z.object({
          reservationId: z.number().optional(),
          apartmentId: z.number(),
          ownerUserId: z.number().optional(),
          guestName: z.string().optional(),
          amount: z.string(),
          paymentMethod: z.enum(['cash', 'bank_transfer', 'zelle', 'venmo', 'other']).optional(),
          notes: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof securityDeposits.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/admin/deposits/:id' as const,
        input: z.object({
          status: z.enum(['collected', 'held', 'returned', 'partially_returned', 'forfeited']).optional(),
          returnedAmount: z.string().optional(),
          notes: z.string().optional(),
        }),
        responses: { 200: z.custom<typeof securityDeposits.$inferSelect>() },
      },
    },
    caja: {
      balance: {
        method: 'GET' as const,
        path: '/api/admin/caja/balance' as const,
        responses: { 200: z.object({
          cashIn: z.string(),
          serviceCharges: z.string(),
          settlements: z.string(),
          depositsHeld: z.string(),
          currentBalance: z.string(),
          transactionCount: z.number(),
        }) },
      },
      transactions: {
        method: 'GET' as const,
        path: '/api/admin/caja/transactions' as const,
        responses: { 200: z.array(z.custom<typeof cashTransactions.$inferSelect>()) },
      },
      logPayment: {
        method: 'POST' as const,
        path: '/api/admin/caja/log-payment' as const,
        input: z.object({
          type: z.enum(['guest_payment', 'admin_fee', 'expense', 'income']),
          category: z.string(),
          amount: z.string(),
          paymentMethod: z.enum(['cash', 'bank_transfer', 'zelle', 'venmo', 'other']).optional(),
          apartmentId: z.number().optional(),
          reservationId: z.number().optional(),
          ownerUserId: z.number().optional(),
          description: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof cashTransactions.$inferSelect>() },
      },
      settle: {
        method: 'POST' as const,
        path: '/api/admin/caja/settle' as const,
        input: z.object({
          ownerUserId: z.number(),
          amount: z.string(),
          paymentMethod: z.enum(['cash', 'bank_transfer', 'zelle', 'venmo', 'other']).optional(),
          description: z.string().optional(),
          periodStart: z.string().optional(),
          periodEnd: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof cashTransactions.$inferSelect>() },
      },
      reconciliation: {
        method: 'GET' as const,
        path: '/api/admin/caja/reconciliation' as const,
        responses: { 200: z.any() },
      },
    },
    orgBillingProfile: {
      get: {
        method: 'GET' as const,
        path: '/api/admin/org-billing-profile' as const,
        responses: { 200: z.any() },
      },
      upsert: {
        method: 'PUT' as const,
        path: '/api/admin/org-billing-profile' as const,
        input: z.object({
          companyName: z.string().min(1),
          taxId: z.string().optional(),
          addressLine1: z.string().optional(),
          addressLine2: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          postalCode: z.string().optional(),
          country: z.string().optional(),
          billingEmail: z.string().optional(),
          billingPhone: z.string().optional(),
          bankName: z.string().optional(),
          bankAccountName: z.string().optional(),
          bankAccountNumber: z.string().optional(),
          bankRoutingNumber: z.string().optional(),
          bankSwiftCode: z.string().optional(),
          zelleName: z.string().optional(),
          zelleEmail: z.string().optional(),
          zellePhone: z.string().optional(),
          venmoHandle: z.string().optional(),
          paypalEmail: z.string().optional(),
        }),
        responses: { 200: z.any() },
      },
    },
    ownerBillingProfiles: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/owner-billing-profiles' as const,
        responses: { 200: z.any() },
      },
      get: {
        method: 'GET' as const,
        path: '/api/admin/owner-billing-profiles/:ownerUserId' as const,
        responses: { 200: z.any() },
      },
      upsert: {
        method: 'PUT' as const,
        path: '/api/admin/owner-billing-profiles/:ownerUserId' as const,
        input: z.object({
          companyName: z.string().min(1),
          taxId: z.string().optional(),
          addressLine1: z.string().optional(),
          addressLine2: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          postalCode: z.string().optional(),
          country: z.string().optional(),
          billingEmail: z.string().optional(),
          billingPhone: z.string().optional(),
          // Auto-invoice override (null = use org default)
          autoInvoicePeriod: z.enum(['monthly', 'biweekly', 'quarterly', 'weekly']).nullable().optional(),
          autoInvoiceDayOfMonth: z.number().int().min(1).max(28).nullable().optional(),
          autoInvoiceDayOfWeek: z.number().int().min(0).max(6).nullable().optional(),
        }),
        responses: { 200: z.any() },
      },
    },
    cleanerBillingProfiles: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/cleaner-billing-profiles' as const,
        responses: { 200: z.any() },
      },
      get: {
        method: 'GET' as const,
        path: '/api/admin/cleaner-billing-profiles/:cleanerUserId' as const,
        responses: { 200: z.any() },
      },
      upsert: {
        method: 'PUT' as const,
        path: '/api/admin/cleaner-billing-profiles/:cleanerUserId' as const,
        input: z.object({
          companyName: z.string().min(1),
          taxId: z.string().optional(),
          addressLine1: z.string().optional(),
          addressLine2: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          postalCode: z.string().optional(),
          country: z.string().optional(),
          billingEmail: z.string().optional(),
          billingPhone: z.string().optional(),
        }),
        responses: { 200: z.any() },
      },
    },
    ownerInvoices: {
      list: { method: 'GET' as const, path: '/api/admin/owner-invoices' as const, responses: { 200: z.any() } },
      get: { method: 'GET' as const, path: '/api/admin/owner-invoices/:id' as const, responses: { 200: z.any() } },
      create: {
        method: 'POST' as const,
        path: '/api/admin/owner-invoices' as const,
        input: z.object({
          ownerUserId: z.number().optional(),
          billedToOrgId: z.number().optional(),
          periodStart: z.string(),
          periodEnd: z.string(),
          notes: z.string().optional(),
        }).refine(d => d.ownerUserId != null || d.billedToOrgId != null, { message: "ownerUserId or billedToOrgId required" }),
        responses: { 201: z.any() },
      },
      createItemized: {
        method: 'POST' as const,
        path: '/api/admin/owner-invoices/itemized' as const,
        input: z.object({
          ownerUserId: z.number().optional(),
          billedToOrgId: z.number().optional(),
          invoiceDate: z.string(),
          lineItems: z.array(z.object({
            description: z.string().trim().min(1, "Description required"),
            category: z.string().trim().min(1, "Category required"),
            amount: z.string().refine(v => !isNaN(Number(v)) && Number(v) !== 0, "Amount must be a non-zero number"),
            date: z.string().optional(),
            quantity: z.number().positive().optional(),
          })).min(1, "At least one line item required"),
          notes: z.string().optional(),
        }).refine(d => d.ownerUserId != null || d.billedToOrgId != null, { message: "ownerUserId or billedToOrgId required" }),
        responses: { 201: z.any() },
      },
      listReceived: { method: 'GET' as const, path: '/api/admin/owner-invoices/received' as const, responses: { 200: z.any() } },
      approve: { method: 'PATCH' as const, path: '/api/admin/owner-invoices/:id/approve' as const, responses: { 200: z.any() } },
      issue: { method: 'PATCH' as const, path: '/api/admin/owner-invoices/:id/issue' as const, responses: { 200: z.any() } },
      update: {
        method: 'PATCH' as const,
        path: '/api/admin/owner-invoices/:id' as const,
        input: z.object({
          notes: z.string().optional(),
          lineItems: z.array(z.object({
            description: z.string(),
            category: z.string(),
            amount: z.string(),
            date: z.string().optional(), financialRecordId: z.number().int().optional(), unitCostId: z.number().int().optional(),
          })).optional(),
        }),
        responses: { 200: z.any() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/owner-invoices/:id' as const,
        responses: { 204: z.any() },
      },
      refund: {
        method: 'POST' as const,
        path: '/api/admin/owner-invoices/:id/refund' as const,
        input: z.object({
          amount: z.string()
            .optional()
            .refine(v => v == null || (!isNaN(Number(v)) && Number(v) > 0), "Amount must be a positive number"),
          reason: z.string().trim().min(1, "Reason is required").max(500),
        }),
        responses: { 200: z.any() },
      },
      revert: {
        method: 'POST' as const,
        path: '/api/admin/owner-invoices/:id/revert' as const,
        input: z.object({
          reason: z.string().trim().min(1, "Reason is required").max(500),
        }),
        responses: { 200: z.any() },
      },
      recordPayment: {
        method: 'POST' as const,
        path: '/api/admin/owner-invoices/:id/record-payment' as const,
        input: z.object({
          amount: z.string().refine(v => !isNaN(Number(v)) && Number(v) > 0, "Amount must be a positive number"),
          paymentMethod: z.enum(["cash", "bank_transfer", "zelle", "venmo", "other"]).optional(),
          notes: z.string().optional(),
        }),
        responses: { 200: z.any() },
      },
      getPayments: {
        method: 'GET' as const,
        path: '/api/admin/owner-invoices/:id/payments' as const,
        responses: { 200: z.any() },
      },
      dashboard: {
        method: 'GET' as const,
        path: '/api/admin/owner-invoices/dashboard' as const,
        responses: { 200: z.any() },
      },
      receivables: {
        method: 'GET' as const,
        path: '/api/admin/owner-invoices/receivables' as const,
        responses: { 200: z.any() },
      },
      receivableDetail: {
        method: 'GET' as const,
        path: '/api/admin/owner-invoices/receivables/:recipientType/:recipientId' as const,
        responses: { 200: z.any() },
      },
    },
  },
  owner: {
    apartments: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/apartments' as const,
        responses: { 200: z.array(z.custom<typeof apartments.$inferSelect>()) },
      },
    },
    submissions: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/submissions' as const,
        responses: { 200: z.array(z.custom<typeof submissions.$inferSelect>()) },
      },
    },
    cleaningJobs: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/cleaning-jobs' as const,
        responses: { 200: z.array(z.custom<typeof cleaningJobs.$inferSelect>()) },
      },
    },
    reservations: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/reservations' as const,
        responses: { 200: z.array(z.custom<typeof reservations.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/owner/reservations' as const,
        input: z.object({
          apartmentId: z.number(),
          guestName: z.string().min(1),
          guestEmail: z.string().optional(),
          guestPhone: z.string().optional(),
          checkInDate: z.string(),
          checkOutDate: z.string(),
          status: z.enum(['confirmed', 'checked_in', 'checked_out', 'cancelled']).optional(),
          notes: z.string().optional(),
          source: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof reservations.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/owner/reservations/:id' as const,
        input: z.object({
          guestName: z.string().optional(),
          guestEmail: z.string().optional(),
          guestPhone: z.string().optional(),
          checkInDate: z.string().optional(),
          checkOutDate: z.string().optional(),
          status: z.enum(['confirmed', 'checked_in', 'checked_out', 'cancelled']).optional(),
          notes: z.string().optional(),
        }),
        responses: { 200: z.custom<typeof reservations.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/owner/reservations/:id' as const,
        responses: { 204: z.void() },
      },
      ical: {
        method: 'GET' as const,
        path: '/api/owner/reservations/ical' as const,
        responses: { 200: z.string() },
      },
    },
    stats: {
      get: {
        method: 'GET' as const,
        path: '/api/owner/stats' as const,
        responses: {
          200: z.object({
            totalApartments: z.number(),
            totalSubmissions: z.number(),
            pendingCleanings: z.number(),
            completedCleanings: z.number(),
            upcomingCheckIns: z.number(),
          }),
        },
      },
    },
    finances: {
      records: {
        method: 'GET' as const,
        path: '/api/owner/finances' as const,
        responses: { 200: z.array(z.custom<typeof financialRecords.$inferSelect>()) },
      },
      summary: {
        method: 'GET' as const,
        path: '/api/owner/finances/summary' as const,
        responses: {
          200: z.object({
            totalCheckInFees: z.string(),
            totalCleaningFees: z.string(),
            totalOwed: z.string(),
            pendingPayments: z.string(),
            recordCount: z.number(),
          }),
        },
      },
    },
    deposits: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/deposits' as const,
        responses: { 200: z.array(z.custom<typeof securityDeposits.$inferSelect>()) },
      },
    },
    caja: {
      reconciliation: {
        method: 'GET' as const,
        path: '/api/owner/caja/reconciliation' as const,
        responses: { 200: z.any() },
      },
    },
    reports: {
      get: {
        method: 'GET' as const,
        path: '/api/owner/reports' as const,
        responses: { 200: z.any() },
      },
    },
    billingProfile: {
      get: {
        method: 'GET' as const,
        path: '/api/owner/billing-profile' as const,
        responses: { 200: z.any() },
      },
    },
    invoices: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/invoices' as const,
        responses: { 200: z.any() },
      },
      get: {
        method: 'GET' as const,
        path: '/api/owner/invoices/:id' as const,
        responses: { 200: z.any() },
      },
      getPayments: {
        method: 'GET' as const,
        path: '/api/owner/invoices/:id/payments' as const,
        responses: { 200: z.any() },
      },
    },
  },
  cleaner: {
    jobs: {
      list: {
        method: 'GET' as const,
        path: '/api/cleaner/jobs' as const,
        responses: { 200: z.array(z.custom<typeof cleaningJobs.$inferSelect>()) },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/cleaner/jobs/:id' as const,
        input: z.object({
          status: z.enum(['pending', 'in_progress', 'completed']),
          notes: z.string().optional(),
          completionPhotos: z.array(z.string()).optional(),
        }),
        responses: { 200: z.custom<typeof cleaningJobs.$inferSelect>() },
      },
    },
    generalTasks: {
      list: {
        method: 'GET' as const,
        path: '/api/cleaner/general-tasks' as const,
        responses: { 200: z.array(z.custom<typeof generalTasks.$inferSelect>()) },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/cleaner/general-tasks/:id' as const,
        input: z.object({
          status: z.enum(['pending', 'in_progress', 'completed']),
          completionNotes: z.string().nullable().optional(),
        }),
        responses: { 200: z.custom<typeof generalTasks.$inferSelect>() },
      },
    },
    apartments: {
      list: {
        method: 'GET' as const,
        path: '/api/cleaner/apartments' as const,
        responses: { 200: z.array(z.custom<typeof apartments.$inferSelect>()) },
      },
    },
    finances: {
      records: {
        method: 'GET' as const,
        path: '/api/cleaner/finances' as const,
        responses: { 200: z.array(z.custom<typeof financialRecords.$inferSelect>()) },
      },
      summary: {
        method: 'GET' as const,
        path: '/api/cleaner/finances/summary' as const,
        responses: {
          200: z.object({
            totalEarnings: z.string(),
            pendingPayments: z.string(),
            paidPayments: z.string(),
            completedJobs: z.number(),
          }),
        },
      },
    },
  },

  platform: {
    organizations: {
      list: {
        method: 'GET' as const,
        path: '/api/platform/organizations' as const,
        responses: { 200: z.array(z.custom<typeof organizations.$inferSelect>()) },
      },
      get: {
        method: 'GET' as const,
        path: '/api/platform/organizations/:id' as const,
        responses: { 200: z.custom<typeof organizations.$inferSelect>() },
      },
      create: {
        method: 'POST' as const,
        path: '/api/platform/organizations' as const,
        input: insertOrganizationSchema,
        responses: { 200: z.custom<typeof organizations.$inferSelect>() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/platform/organizations/:id' as const,
        input: insertOrganizationSchema.partial(),
        responses: { 200: z.custom<typeof organizations.$inferSelect>() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/platform/organizations/:id' as const,
        responses: { 200: z.void() },
      },
    },
    stats: {
      method: 'GET' as const,
      path: '/api/platform/stats' as const,
      responses: {
        200: z.object({
          totalOrganizations: z.number(),
          activeOrganizations: z.number(),
          totalApartments: z.number(),
          totalUsers: z.number(),
        }),
      },
    },
    users: {
      list: {
        method: 'GET' as const,
        path: '/api/platform/users' as const,
        responses: { 200: z.array(z.custom<typeof users.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/platform/users' as const,
        input: insertUserSchema,
        responses: { 200: z.custom<typeof users.$inferSelect>() },
      },
    },
    billing: {
      getPlan: {
        method: 'GET' as const,
        path: '/api/platform/organizations/:id/billing-plan' as const,
        responses: { 200: z.custom<typeof billingPlans.$inferSelect>().nullable() },
      },
      upsertPlan: {
        method: 'PUT' as const,
        path: '/api/platform/organizations/:id/billing-plan' as const,
        input: z.object({
          model: z.enum(['flat', 'per_apartment', 'per_usage', 'custom']),
          currency: z.string().optional(),
          flatAmount: z.string().optional(),
          perApartmentAmount: z.string().optional(),
          perUsageAmount: z.string().optional(),
          customAmount: z.string().optional(),
          billingCycle: z.enum(['monthly', 'quarterly', 'yearly']).optional(),
          isActive: z.boolean().optional(),
          notes: z.string().optional(),
        }),
        responses: { 200: z.custom<typeof billingPlans.$inferSelect>() },
      },
      invoices: {
        method: 'GET' as const,
        path: '/api/platform/organizations/:id/invoices' as const,
        responses: { 200: z.array(z.custom<typeof billingInvoices.$inferSelect>()) },
      },
      allInvoices: {
        method: 'GET' as const,
        path: '/api/platform/invoices' as const,
        responses: { 200: z.array(z.custom<typeof billingInvoices.$inferSelect>()) },
      },
      generateInvoice: {
        method: 'POST' as const,
        path: '/api/platform/organizations/:id/invoices/generate' as const,
        input: z.object({
          periodStart: z.string(),
          periodEnd: z.string(),
          adjustments: z.string().optional(),
          notes: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof billingInvoices.$inferSelect>() },
      },
      updateInvoice: {
        method: 'PATCH' as const,
        path: '/api/platform/invoices/:id' as const,
        input: z.object({
          status: z.enum(['draft', 'issued', 'paid', 'overdue', 'void']).optional(),
          notes: z.string().optional(),
          adjustments: z.string().optional(),
        }),
        responses: { 200: z.custom<typeof billingInvoices.$inferSelect>() },
      },
    },
    features: {
      get: {
        method: 'GET' as const,
        path: '/api/platform/organizations/:id/features' as const,
        responses: { 200: z.any() },
      },
      set: {
        method: 'PUT' as const,
        path: '/api/platform/organizations/:id/features' as const,
        input: z.object({
          featureKey: z.string(),
          enabled: z.boolean(),
        }),
        responses: { 200: z.any() },
      },
      bulkSet: {
        method: 'PUT' as const,
        path: '/api/platform/organizations/:id/features/bulk' as const,
        input: z.object({
          features: z.array(z.object({
            featureKey: z.string(),
            enabled: z.boolean(),
          })),
        }),
        responses: { 200: z.any() },
      },
    },
    marketplace: {
      list: {
        method: 'GET' as const,
        path: '/api/platform/marketplace/apartments' as const,
        responses: { 200: z.array(z.any()) },
      },
      update: {
        method: 'PATCH' as const,
        path: '/api/platform/marketplace/apartments/:id' as const,
        input: z.object({
          marketplaceVisible: z.boolean().optional(),
          marketplaceFeatured: z.boolean().optional(),
          marketplaceFeaturedUntil: z.string().nullable().optional(),
          marketplaceDescription: z.string().nullable().optional(),
          marketplacePrice: z.string().nullable().optional(),
          propertyType: z.string().nullable().optional(),
          amenities: z.string().nullable().optional(),
          location: z.string().nullable().optional(),
        }),
        responses: { 200: z.any() },
      },
      stats: {
        method: 'GET' as const,
        path: '/api/platform/marketplace/stats' as const,
        responses: { 200: z.any() },
      },
    },
  },
  adminBilling: {
    plan: {
      method: 'GET' as const,
      path: '/api/admin/billing/plan' as const,
      responses: { 200: z.custom<typeof billingPlans.$inferSelect>().nullable() },
    },
    invoices: {
      method: 'GET' as const,
      path: '/api/admin/billing/invoices' as const,
      responses: { 200: z.array(z.custom<typeof billingInvoices.$inferSelect>()) },
    },
  },

  notifications: {
    list: {
      method: 'GET' as const,
      path: '/api/notifications' as const,
      responses: { 200: z.array(z.custom<typeof notifications.$inferSelect>()) },
    },
    unreadCount: {
      method: 'GET' as const,
      path: '/api/notifications/unread-count' as const,
      responses: { 200: z.object({ count: z.number() }) },
    },
    markRead: {
      method: 'POST' as const,
      path: '/api/notifications/:id/read' as const,
      responses: { 200: z.object({ success: z.boolean() }) },
    },
    markAllRead: {
      method: 'POST' as const,
      path: '/api/notifications/read-all' as const,
      responses: { 200: z.object({ success: z.boolean() }) },
    },
  },

  push: {
    vapidKey: {
      method: 'GET' as const,
      path: '/api/push/vapid-key' as const,
      responses: { 200: z.object({ publicKey: z.string() }) },
    },
    subscribe: {
      method: 'POST' as const,
      path: '/api/push/subscribe' as const,
      input: z.object({
        endpoint: z.string(),
        p256dhKey: z.string(),
        authKey: z.string(),
        deviceLabel: z.string().optional(),
      }),
      responses: { 201: z.custom<typeof pushSubscriptions.$inferSelect>() },
    },
    unsubscribe: {
      method: 'DELETE' as const,
      path: '/api/push/unsubscribe' as const,
      input: z.object({ endpoint: z.string() }),
      responses: { 200: z.object({ success: z.boolean() }) },
    },
  },

  guestCheckin: {
    submit: {
      method: 'POST' as const,
      path: '/api/guest/:slug/checkin' as const,
      input: z.object({
        guestName: z.string().min(1),
        guestPhone: z.string(),
        guestEmail: z.string().optional(),
        rulesAccepted: z.boolean(),
        notes: z.string().optional(),
        photoUrls: z.array(z.string()).optional(),
      }),
      responses: {
        201: z.custom<typeof checkinSubmissions.$inferSelect>(),
        409: z.object({ message: z.string() }),
        403: errorSchemas.unauthorized,
      },
    },
  },

  guestTickets: {
    create: {
      method: 'POST' as const,
      path: '/api/guest/:slug/tickets' as const,
      input: z.object({
        guestName: z.string().min(1),
        guestEmail: z.string().optional(),
        subject: z.string().min(1),
        category: z.string().optional(),
        body: z.string().min(1),
      }),
      responses: {
        201: z.custom<typeof tickets.$inferSelect>(),
        403: errorSchemas.unauthorized,
      },
    },
  },

  tickets: {
    admin: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/tickets' as const,
        responses: { 200: z.array(z.custom<typeof tickets.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/tickets' as const,
        input: z.object({
          subject: z.string().min(1),
          category: z.string().optional(),
          priority: z.enum(['low', 'medium', 'high']).optional(),
          apartmentId: z.number().optional(),
          reservationId: z.number().optional(),
          body: z.string().optional(),
        }),
        responses: { 201: z.custom<typeof tickets.$inferSelect>() },
      },
      get: {
        method: 'GET' as const,
        path: '/api/admin/tickets/:id' as const,
        responses: { 200: z.custom<typeof tickets.$inferSelect>() },
      },
      update: {
        method: 'PATCH' as const,
        path: '/api/admin/tickets/:id' as const,
        input: z.object({
          status: z.enum(['open', 'in_progress', 'closed']).optional(),
          assignedToUserId: z.number().nullable().optional(),
          priority: z.enum(['low', 'medium', 'high']).optional(),
        }),
        responses: { 200: z.custom<typeof tickets.$inferSelect>() },
      },
      messages: {
        list: {
          method: 'GET' as const,
          path: '/api/admin/tickets/:id/messages' as const,
          responses: { 200: z.array(z.custom<typeof ticketMessages.$inferSelect>()) },
        },
        create: {
          method: 'POST' as const,
          path: '/api/admin/tickets/:id/messages' as const,
          input: z.object({
            body: z.string().min(1),
            attachmentUrls: z.array(z.string()).optional(),
          }),
          responses: { 201: z.custom<typeof ticketMessages.$inferSelect>() },
        },
      },
    },
    owner: {
      list: {
        method: 'GET' as const,
        path: '/api/owner/tickets' as const,
        responses: { 200: z.array(z.custom<typeof tickets.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/owner/tickets' as const,
        input: z.object({
          subject: z.string().min(1),
          category: z.string().optional(),
          body: z.string().optional(),
          apartmentId: z.number().optional(),
        }),
        responses: { 201: z.custom<typeof tickets.$inferSelect>() },
      },
    },
    cleaner: {
      list: {
        method: 'GET' as const,
        path: '/api/cleaner/tickets' as const,
        responses: { 200: z.array(z.custom<typeof tickets.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/cleaner/tickets' as const,
        input: z.object({
          subject: z.string().min(1),
          category: z.string().optional(),
          body: z.string().optional(),
          apartmentId: z.number().optional(),
          cleaningJobId: z.number().optional(),
        }),
        responses: { 201: z.custom<typeof tickets.$inferSelect>() },
      },
    },
    laundry: {
      list: {
        method: 'GET' as const,
        path: '/api/laundry/tickets' as const,
        responses: { 200: z.array(z.custom<typeof tickets.$inferSelect>()) },
      },
      create: {
        method: 'POST' as const,
        path: '/api/laundry/tickets' as const,
        input: z.object({
          subject: z.string().min(1),
          category: z.string().optional(),
          body: z.string().optional(),
          apartmentId: z.number().optional(),
          laundryJobId: z.number().optional(),
        }),
        responses: { 201: z.custom<typeof tickets.$inferSelect>() },
      },
    },
  },

  orgModules: {
    get: {
      method: 'GET' as const,
      path: '/api/org/modules' as const,
      responses: {
        200: z.object({
          modules: z.array(z.string()),
        }),
      },
    },
  },

  booking: {
    pages: {
      list: {
        method: 'GET' as const,
        path: '/api/booking/pages' as const,
        responses: { 200: z.any() },
      },
      get: {
        method: 'GET' as const,
        path: '/api/booking/pages/:id' as const,
        responses: { 200: z.any(), 404: errorSchemas.notFound },
      },
      create: {
        method: 'POST' as const,
        path: '/api/booking/pages' as const,
        responses: { 201: z.any() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/booking/pages/:id' as const,
        responses: { 200: z.any() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/booking/pages/:id' as const,
        responses: { 204: z.void() },
      },
      ratePlans: {
        method: 'GET' as const,
        path: '/api/booking/pages/:id/rate-plans' as const,
        responses: { 200: z.any() },
      },
    },
    ratePlans: {
      get: {
        method: 'GET' as const,
        path: '/api/booking/rate-plans/:id' as const,
        responses: { 200: z.any(), 404: errorSchemas.notFound },
      },
      create: {
        method: 'POST' as const,
        path: '/api/booking/rate-plans' as const,
        responses: { 201: z.any() },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/booking/rate-plans/:id' as const,
        responses: { 200: z.any() },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/booking/rate-plans/:id' as const,
        responses: { 204: z.void() },
      },
    },
    public: {
      getPage: {
        method: 'GET' as const,
        path: '/api/public/booking/:orgSlug/:pageSlug' as const,
        responses: { 200: z.any(), 404: errorSchemas.notFound },
      },
      search: {
        method: 'POST' as const,
        path: '/api/public/booking/:orgSlug/:pageSlug/search' as const,
        responses: { 200: z.any(), 404: errorSchemas.notFound },
      },
      hold: {
        method: 'POST' as const,
        path: '/api/public/booking/hold' as const,
        responses: { 201: z.any(), 400: errorSchemas.validation },
      },
      checkout: {
        method: 'POST' as const,
        path: '/api/public/booking/checkout' as const,
        responses: { 201: z.any(), 400: errorSchemas.validation },
      },
      holdStatus: {
        method: 'GET' as const,
        path: '/api/public/booking/hold/:id' as const,
        responses: { 200: z.any(), 404: errorSchemas.notFound },
      },
      confirm: {
        method: 'POST' as const,
        path: '/api/public/booking/confirm' as const,
        responses: { 200: z.any(), 400: errorSchemas.validation, 402: errorSchemas.validation },
      },
    },
  },
  qa: {
    startInspection: {
      method: 'POST' as const,
      path: '/api/qa/inspections/:jobId/start' as const,
      responses: { 201: z.any(), 404: errorSchemas.notFound },
    },
    scan: {
      method: 'POST' as const,
      path: '/api/qa/scan' as const,
      responses: { 200: z.any(), 400: errorSchemas.validation },
    },
    getInspection: {
      method: 'GET' as const,
      path: '/api/qa/inspection/:jobId' as const,
      responses: { 200: z.any(), 404: errorSchemas.notFound },
    },
    getReport: {
      method: 'GET' as const,
      path: '/api/qa/report/:jobId' as const,
      responses: { 200: z.any() },
    },
    getCriteria: {
      method: 'GET' as const,
      path: '/api/qa/criteria/:unitId' as const,
      responses: { 200: z.any() },
    },
    updateCriteria: {
      method: 'PUT' as const,
      path: '/api/qa/criteria/:unitId' as const,
      responses: { 200: z.any() },
    },
    cleanerFeatures: {
      method: 'GET' as const,
      path: '/api/cleaner/features' as const,
      responses: { 200: z.object({ qa_vision: z.boolean() }) },
    },
    listTemplates: {
      method: 'GET' as const,
      path: '/api/qa/templates' as const,
      responses: { 200: z.any() },
    },
    createTemplate: {
      method: 'POST' as const,
      path: '/api/qa/templates' as const,
      responses: { 201: z.any() },
    },
    updateTemplate: {
      method: 'PUT' as const,
      path: '/api/qa/templates/:id' as const,
      responses: { 200: z.any() },
    },
    deleteTemplate: {
      method: 'DELETE' as const,
      path: '/api/qa/templates/:id' as const,
      responses: { 200: z.any() },
    },
    setDefaultTemplate: {
      method: 'PUT' as const,
      path: '/api/qa/templates/:id/default' as const,
      responses: { 200: z.any() },
    },
    applyTemplate: {
      method: 'POST' as const,
      path: '/api/qa/templates/:id/apply' as const,
      responses: { 200: z.any() },
    },
    clearUnitZones: {
      method: 'DELETE' as const,
      path: '/api/qa/criteria/:unitId/zones' as const,
      responses: { 200: z.any() },
    },
  },

  crossOrg: {
    contracts: {
      list: {
        method: 'GET' as const,
        path: '/api/cross-org/contracts' as const,
        responses: {
          200: z.object({
            asProvider: z.array(z.custom<typeof orgContracts.$inferSelect>()),
            asConsumer: z.array(z.custom<typeof orgContracts.$inferSelect>()),
          }),
        },
      },
      grants: {
        method: 'GET' as const,
        path: '/api/cross-org/contracts/:id/grants' as const,
        responses: {
          200: z.array(z.custom<typeof orgDataGrants.$inferSelect>()),
          404: z.object({ message: z.string() }),
        },
      },
      propose: {
        method: 'POST' as const,
        path: '/api/cross-org/contracts' as const,
        input: z.object({
          counterpartyOrgId: z.number().int().positive(),
          callerRole: z.enum(['provider', 'consumer']),
          contractType: z.enum(['service_engagement', 'internal_management']),
          title: z.string().min(1),
          terms: z.record(z.unknown()),
          endsAt: z.string(),
          startsAt: z.string().optional(),
        }),
        responses: {
          201: z.custom<typeof orgContracts.$inferSelect>(),
          400: z.object({ message: z.string(), fields: z.array(z.string()).optional() }),
          403: errorSchemas.unauthorized,
        },
      },
      accept: {
        method: 'POST' as const,
        path: '/api/cross-org/contracts/:id/accept' as const,
        responses: {
          200: z.custom<typeof orgContracts.$inferSelect>(),
          400: z.object({ message: z.string() }),
          403: errorSchemas.unauthorized,
          404: errorSchemas.notFound,
        },
      },
      reject: {
        method: 'POST' as const,
        path: '/api/cross-org/contracts/:id/reject' as const,
        input: z.object({ reason: z.string().optional() }),
        responses: {
          200: z.custom<typeof orgContracts.$inferSelect>(),
          400: z.object({ message: z.string() }),
          403: errorSchemas.unauthorized,
          404: errorSchemas.notFound,
        },
      },
      proposeAmendment: {
        method: 'POST' as const,
        path: '/api/cross-org/contracts/:id/propose-amendment' as const,
        input: z.object({
          title: z.string().min(1),
          terms: z.record(z.unknown()),
          endsAt: z.string(),
        }),
        responses: {
          201: z.custom<typeof orgContracts.$inferSelect>(),
          400: z.object({ message: z.string() }),
          403: errorSchemas.unauthorized,
          404: errorSchemas.notFound,
        },
      },
      terminate: {
        method: 'POST' as const,
        path: '/api/cross-org/contracts/:id/terminate' as const,
        input: z.object({ reason: z.string().optional() }),
        responses: {
          200: z.custom<typeof orgContracts.$inferSelect>(),
          400: z.object({ message: z.string() }),
          403: errorSchemas.unauthorized,
          404: errorSchemas.notFound,
        },
      },
      summary: {
        method: 'GET' as const,
        path: '/api/cross-org/contracts/:id/summary' as const,
        responses: {
          200: z.object({
            contract: z.custom<typeof orgContracts.$inferSelect>(),
            callerRole: z.enum(['provider', 'consumer']),
            counterpartyOrg: z.object({ id: z.number(), name: z.string() }),
            apartments: z.array(z.object({ id: z.number(), name: z.string(), bedrooms: z.number().nullable() })),
            stats: z.object({
              jobsCompleted: z.number(),
              jobsPending: z.number(),
              jobsCancelled: z.number(),
              totalEarned: z.string(),
              totalPending: z.string(),
              totalInvoiced: z.string(),
              totalOutstanding: z.string(),
            }),
            recentJobs: z.array(z.object({
              id: z.number(),
              apartmentName: z.string(),
              status: z.string(),
              scheduledDate: z.string().nullable(),
              completedAt: z.string().nullable(),
              assigneeName: z.string().nullable(),
            })),
            invoices: z.array(z.object({
              id: z.number(),
              invoiceNumber: z.string(),
              status: z.string(),
              subtotal: z.string(),
              platformFeeAmount: z.string(),
              total: z.string(),
              periodStart: z.string(),
              periodEnd: z.string(),
            })),
          }),
          403: errorSchemas.unauthorized,
          404: errorSchemas.notFound,
        },
      },
    },
  },
  stripeConnect: {
    status: {
      method: 'GET' as const,
      path: '/api/admin/stripe-connect/status' as const,
      responses: {
        200: z.object({
          status: z.enum(['not_started', 'pending', 'complete', 'restricted']),
          stripeConnectedAccountId: z.string().nullable(),
          chargesEnabled: z.boolean(),
          payoutsEnabled: z.boolean(),
          detailsSubmitted: z.boolean(),
          requirementsDue: z.array(z.string()),
        }),
        503: z.object({ message: z.string() }),
      },
    },
    start: {
      method: 'POST' as const,
      path: '/api/admin/stripe-connect/start' as const,
      responses: {
        200: z.object({ url: z.string().url() }),
        503: z.object({ message: z.string() }),
      },
    },
    refresh: {
      method: 'POST' as const,
      path: '/api/admin/stripe-connect/refresh' as const,
      responses: {
        200: z.object({
          status: z.enum(['not_started', 'pending', 'complete', 'restricted']),
          stripeConnectedAccountId: z.string().nullable(),
          chargesEnabled: z.boolean(),
          payoutsEnabled: z.boolean(),
          detailsSubmitted: z.boolean(),
          requirementsDue: z.array(z.string()),
        }),
        503: z.object({ message: z.string() }),
      },
    },
    loginLink: {
      method: 'POST' as const,
      path: '/api/admin/stripe-connect/login-link' as const,
      responses: {
        200: z.object({ url: z.string().url() }),
        400: z.object({ message: z.string() }),
        503: z.object({ message: z.string() }),
      },
    },
  },
};

export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}

export type { 
  Organization, InsertOrganization,
  User, InsertBuilding, InsertApartment, InsertSection, InsertChecklistItem, 
  InsertPhotoRequirement, InsertCleaningTask, InsertCleaningJob, InsertSubmission,
  InsertReservation, Reservation, CommissionSettings, FinancialRecord, InsertFinancialRecord,
  CreateSubmissionRequest, Apartment, Building, Section, CleaningJob, CleaningTask, Submission,
  GeneralTask, InsertGeneralTask
} from './schema';
